import { useCallback, useRef } from 'react';

import { useSession } from '@/providers/SessionProvider/SessionProvider';
import { getUserRole } from '@/utils/roleUtils';

// TODO: feature_flag_cleanup: hasAccountPermissions - Remove this import when FF is fully rolled out
import { hasRoleForPermission } from './rbacFallback';
import useGetClientId from './useGetClientId';

/**
 * Hook for checking permissions programmatically.
 * Controlled by the `hasAccountPermissions` feature flag.
 *
 * When feature flag is ON: Uses PBAC (Permission-Based Access Control)
 * When feature flag is OFF: Falls back to RBAC (Role-Based Access Control)
 *
 * @example
 * const { hasPermission } = usePermissionAccess();
 *
 * if (hasPermission('campaigns-edit')) {
 *   // User can edit campaigns
 * }
 *
 * // Multiple checks
 * if (hasPermission('campaigns-edit') && hasPermission('campaigns-delete')) {
 *   // User can edit AND delete
 * }
 */
const usePermissionAccess = () => {
  const {
    isCurrentClientLoading,
    isOrgUsersLoading,
    currentClient,
    currentUserAccounts,
    isUserPermissionsLoading,
    // TODO: feature_flag_cleanup: hasAccountPermissions - Remove accountLevelRoles when FF is fully rolled out
    accountLevelRoles,
  } = useSession();
  const clientId = useGetClientId();

  const isLoading =
    isCurrentClientLoading || isOrgUsersLoading || isUserPermissionsLoading;

  // Track if we've ever finished loading for the current client
  const hasLoadedOnce = useRef(false);
  const previousClientId = useRef(clientId);

  // Reset hasLoadedOnce when client changes (account switching)
  // Use else-if to ensure these are mutually exclusive
  if (previousClientId.current !== clientId) {
    hasLoadedOnce.current = false;
    previousClientId.current = clientId;
  } else if (!isLoading) {
    hasLoadedOnce.current = true;
  }

  const currentClientName = currentClient?.name ?? '';
  const hasAccountPermissions = currentClient?.hasAccountPermissions ?? false;
  // TODO: feature_flag_cleanup: hasAccountPermissions - Remove userRole when FF is fully rolled out
  const userRole = !accountLevelRoles ? null : getUserRole(accountLevelRoles);

  /**
   * Check if the user has a specific permission.
   * @param permission - Permission string (e.g., 'campaigns-edit', 'creatives-view')
   */
  const hasPermission = useCallback(
    (permission: string): boolean => {
      // TODO: feature_flag_cleanup: hasAccountPermissions - Remove this RBAC fallback block when FF is fully rolled out
      if (!hasAccountPermissions) {
        return hasRoleForPermission(permission, userRole);
      }

      // If no client selected, deny access
      if (!clientId) return false;

      const currentAccount = currentUserAccounts.get(currentClientName);
      if (!currentAccount) return false;
      return currentAccount.permissions.has(permission);
    },
    [
      hasAccountPermissions,
      userRole,
      clientId,
      currentClientName,
      currentUserAccounts,
    ],
  );

  return {
    hasPermission,
    isLoading,
    isInitialLoad: isLoading && !hasLoadedOnce.current,
  };
};

export default usePermissionAccess;
